import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { blogAPI, newsAPI } from "../services/api";
import TempleStats from "../components/Statistics/TempleStats";
import TeamSection from "../components/TeamSection";
import ScrollReveal, { RevealUp, RevealLeft, RevealRight, RevealScale, StaggeredReveal } from "../components/ScrollReveal";

// Floating petal component
const FloatingPetals = () => {
  const petals = ["🌸", "🪷", "🌺", "🌼", "🌻"];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {Array.from({ length: 12 }).map((_, i) => (
        <span
          key={i}
          className="petal absolute"
          style={{
            left: `${(i * 8.5) % 100}%`,
            animationDuration: `${7 + (i % 5) * 1.5}s`,
            animationDelay: `${(i * 0.8) % 6}s`,
            fontSize: `${0.8 + (i % 3) * 0.3}rem`,
            opacity: 0.5 + (i % 4) * 0.1,
          }}
        >
          {petals[i % petals.length]}
        </span>
      ))}
    </div>
  );
};

const DOHAS = [
  {
    sanskrit: "पुष्पवाटिका मध्ये तु सीता राम मिलन स्थलम्।\nप्रथम दर्शन पावनं तत् फुलहर धाम उत्तमम्॥",
    hindi: "पुष्प वाटिका के मध्य में सीता-राम का मिलन स्थल है।\nप्रथम दर्शन का पावन स्थान, फुलहर धाम उत्तम है॥",
  },
  {
    sanskrit: "गिरिजा पूजन स्थले तस्मिन् मिथिला संस्कृति रक्षकम्।\nभक्ति भाव से पूर्णं तत् तीर्थ क्षेत्र महत्तमम्॥",
    hindi: "गिरिजा पूजन के उस स्थान में मिथिला संस्कृति का रक्षक है।\nभक्ति भाव से पूर्ण वह तीर्थ क्षेत्र महान है॥",
  },
  {
    sanskrit: "बाग तड़ाग बिलोकि प्रभु, हरषे बंधु समेत।\nपरम रम्य आराम यह, जो रामहि सुख देत॥",
    hindi: "बाग और तालाब देखकर प्रभु राम भाइयों सहित हर्षित हुए।\nयह परम रमणीय बगीचा वही है जो राम को सुख देता है॥",
    source: "— श्रीरामचरितमानस, गोस्वामी तुलसीदास",
  },
];

const Home = () => {
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDoha, setCurrentDoha] = useState(0);
  const [dohaFading, setDohaFading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogsRes, newsRes] = await Promise.all([blogAPI.getAll(), newsAPI.getAll()]);
        setRecentBlogs((blogsRes.data.blogs || blogsRes.data || []).slice(0, 3));
        setRecentNews((newsRes.data.news || newsRes.data || []).slice(0, 3));
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const interval = setInterval(() => {
      setDohaFading(true);
      setTimeout(() => {
        setCurrentDoha((p) => (p + 1) % DOHAS.length);
        setDohaFading(false);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#FEF6E4" }}>

      {/* ══════════ HERO ══════════ */}
      <section className="hero-sacred-bg relative min-h-[92vh] flex flex-col items-center justify-center overflow-hidden py-20 px-4">
        <FloatingPetals />

        {/* Mandala bg decoration */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-5">
          <div className="text-white" style={{ fontSize: "60vw", lineHeight: 1 }}>🕉️</div>
        </div>

        <RevealScale className="relative z-10 max-w-3xl mx-auto text-center">
          {/* Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(244,165,0,0.2)', color: '#FFBE2E', border: '1px solid rgba(244,165,0,0.3)' }}>
              🗺️ रामायण सर्किट — बिहार सरकार
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#FEF6E4', border: '1px solid rgba(255,255,255,0.2)' }}>
              🏛️ NGO: BR/2025/0853517
            </span>
          </div>

          {/* Title */}
          <h1 className="font-serif font-bold text-white leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 6vw, 4.5rem)' }}>
            सीता राम प्रथम मिलन
          </h1>
          <h2 className="font-serif font-bold leading-tight mb-6 text-shimmer"
            style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>
            पुष्प वाटिका मंदिर
          </h2>
          <p className="text-white/80 text-base sm:text-lg mb-2 font-medium">
            "गिरिजा पूजन की पावन स्थली" — मिथिला का महाकुंभ
          </p>
          <p className="text-white/60 text-sm mb-10">
            ग्राम + पोo — फुलहर धाम, हरलाखी, मधुबनी, बिहार — 847240
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/about" className="btn-temple-secondary px-8 py-3.5 rounded-2xl font-bold text-base">
              हमारे बारे में जानें →
            </Link>
            <Link to="/donate" className="btn-donation px-8 py-3.5 rounded-2xl font-bold text-base">
              💰 दान करें
            </Link>
          </div>
        </RevealScale>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce-gentle">
          <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center pt-1.5">
            <div className="w-1 h-3 rounded-full bg-white/60" />
          </div>
        </div>
      </section>

      {/* ══════════ DOHA CAROUSEL ══════════ */}
      <section className="py-14 sm:py-16" style={{ background: 'linear-gradient(135deg, #8B1A1A, #C44E0E, #F4A500)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-6">श्लोक</p>
          <div style={{ minHeight: '120px', transition: 'opacity 0.5s ease', opacity: dohaFading ? 0 : 1 }}>
            <p className="text-white font-serif text-base sm:text-xl leading-relaxed whitespace-pre-line mb-4">
              {DOHAS[currentDoha].sanskrit}
            </p>
            <div className="w-16 h-0.5 mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.3)' }} />
            <p className="text-white/75 text-sm sm:text-base leading-relaxed whitespace-pre-line">
              {DOHAS[currentDoha].hindi}
            </p>
            {DOHAS[currentDoha].source && (
              <p className="text-white/50 text-xs mt-3 italic">{DOHAS[currentDoha].source}</p>
            )}
          </div>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {DOHAS.map((_, i) => (
              <button key={i} onClick={() => setCurrentDoha(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === currentDoha ? '24px' : '8px',
                  height: '8px',
                  background: i === currentDoha ? '#FFBE2E' : 'rgba(255,255,255,0.3)',
                }} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ TRUST OFFICIAL INFO ══════════ */}
      <section className="py-10 sm:py-14" style={{ background: '#FDF0D5' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <RevealUp className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-temple-primary mb-2">आधिकारिक विवरण</p>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-temple-text">ट्रस्ट परिचय</h2>
            <div className="section-divider" />
          </RevealUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StaggeredReveal>
              {[
                {
                  icon: "🏛️",
                  title: "ट्रस्ट का नाम",
                  content: "सीता राम प्रथम मिलन, पुष्प वाटिका मंदिर तीर्थ क्षेत्र ट्रस्ट (फुलहर धाम)",
                  sub: "Indian Trusts Act 1882 के अंतर्गत पंजीकृत",
                },
                {
                  icon: "📜",
                  title: "पंजीकरण विवरण",
                  content: null,
                  items: [
                    { label: "NGO DARPAN / NITI AAYOG", value: "BR/2025/0853517" },
                    { label: "TRUST REG. NO.", value: "746" },
                  ],
                },
                {
                  icon: "📍",
                  title: "मुख्य कार्यालय",
                  content: "ग्राम + पोo — फुलहर धाम\nहरलाखी, मधुबनी — 847240 (बिहार)",
                  contacts: [
                    { icon: "📞", text: "+91 9470820521" },
                    { icon: "✉️", text: "shreesitaramjimandir@gmail.com" },
                  ],
                },
              ].map((card, i) => (
                <div key={i} className="trust-badge p-6">
                  <div className="text-3xl mb-3">{card.icon}</div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-temple-text-muted mb-3">{card.title}</h4>
                  {card.content && (
                    <p className="text-sm font-semibold text-temple-text leading-snug whitespace-pre-line mb-2">{card.content}</p>
                  )}
                  {card.sub && <p className="text-xs text-temple-primary font-semibold">{card.sub}</p>}
                  {card.items && card.items.map((item, j) => (
                    <div key={j} className={j > 0 ? "pt-3 mt-3 border-t border-temple-secondary/15" : ""}>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-temple-text-muted">{item.label}</p>
                      <p className="font-mono font-bold text-lg text-temple-primary">{item.value}</p>
                    </div>
                  ))}
                  {card.contacts && card.contacts.map((c, j) => (
                    <p key={j} className="text-sm font-semibold text-temple-text mt-2">{c.icon} {c.text}</p>
                  ))}
                </div>
              ))}
            </StaggeredReveal>
          </div>

          {/* Development banner */}
          <RevealUp className="mt-10" delay={400}>
            <div className="rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left"
              style={{ background: 'linear-gradient(135deg, rgba(139,26,26,0.08), rgba(244,165,0,0.08))', border: '1px solid rgba(244,165,0,0.2)' }}>
              <span className="text-3xl flex-shrink-0">🏗️</span>
              <div>
                <p className="font-bold text-temple-text text-sm sm:text-base">
                  बिहार सरकार द्वारा ₹31.13 करोड़ की विकास परियोजना स्वीकृत
                </p>
                <p className="text-temple-text-muted text-xs sm:text-sm mt-1">
                  बाग तराग पुष्प वाटिका सौंदर्यीकरण · गिरिजा स्थान परिसर · लेजर फाउंटेन · घाट विकास · पार्किंग व शौचालय सुविधाएं
                </p>
              </div>
              <span className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #1E5B47, #2D7A60)' }}>
                ✅ प्रगति पर
              </span>
            </div>
          </RevealUp>
        </div>
      </section>

      {/* ══════════ STATISTICS ══════════ */}
      <section className="py-10 sm:py-14" style={{ background: '#FEF6E4' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <RevealUp className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-temple-primary mb-2">आंकड़े</p>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-temple-text">फुलहर धाम की महत्ता</h2>
            <div className="section-divider" />
            <p className="text-temple-text-muted mt-4 max-w-xl mx-auto text-sm">
              मिथिला की इस पावन भूमि के महत्व को दर्शाने वाले प्रमुख तथ्य
            </p>
          </RevealUp>
          <ScrollReveal animation="scale" delay={200}>
            <TempleStats />
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════ TEAM ══════════ */}
      <TeamSection />

      {/* ══════════ RECENT BLOGS ══════════ */}
      {!loading && recentBlogs.length > 0 && (
        <section className="py-10 sm:py-14" style={{ background: '#FDF0D5' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-5">
            <RevealUp className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-widest text-temple-primary mb-2">आध्यात्मिक लेख</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-temple-text">नवीनतम ब्लॉग</h2>
              <div className="section-divider" />
            </RevealUp>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <StaggeredReveal>
                {recentBlogs.map((blog) => (
                  <div key={blog._id} className="temple-card p-6">
                    <h3 className="font-serif text-lg font-bold text-temple-text mb-3 line-clamp-2">{blog.title}</h3>
                    <p className="text-temple-text-muted text-sm mb-4 line-clamp-3">
                      {blog.content?.substring(0, 120)}...
                    </p>
                    <Link to={`/blog/${blog._id}`}
                      className="inline-flex items-center gap-1 text-sm font-bold text-temple-primary hover:text-temple-primary-dark transition-colors">
                      पूरा पढ़ें <span>→</span>
                    </Link>
                  </div>
                ))}
              </StaggeredReveal>
            </div>
            <RevealUp className="text-center mt-10" delay={400}>
              <Link to="/blog" className="btn-temple-primary px-8 py-3 rounded-2xl font-bold text-sm">
                सभी ब्लॉग देखें
              </Link>
            </RevealUp>
          </div>
        </section>
      )}

      {/* ══════════ RECENT NEWS ══════════ */}
      {!loading && recentNews.length > 0 && (
        <section className="py-10 sm:py-14" style={{ background: '#FAE8C8' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-5">
            <RevealUp className="text-center mb-8">
              <p className="text-xs font-bold uppercase tracking-widest text-temple-primary mb-2">समाचार</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-temple-text">ताज़ा समाचार</h2>
              <div className="section-divider" />
            </RevealUp>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentNews.map((news) => (
                <RevealUp key={news._id}>
                  <div className="temple-card p-6">
                    <h3 className="font-serif text-lg font-bold text-temple-text mb-3 line-clamp-2">{news.title}</h3>
                    <p className="text-temple-text-muted text-sm mb-4 line-clamp-3">
                      {news.content?.substring(0, 120)}...
                    </p>
                    <span className="text-xs font-bold px-3 py-1 rounded-full"
                      style={{ background: 'rgba(244,165,0,0.15)', color: '#C9860A' }}>
                      📰 समाचार
                    </span>
                  </div>
                </RevealUp>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/news" className="btn-temple-primary px-8 py-3 rounded-2xl font-bold text-sm">
                सभी समाचार देखें
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ══════════ CONTACT CTA ══════════ */}
      <section className="py-16 sm:py-24 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #2C1408 0%, #8B1A1A 45%, #C44E0E 80%, #F4A500 100%)' }}>
        <div className="absolute inset-0 opacity-5 pointer-events-none flex items-center justify-center">
          <div className="text-white" style={{ fontSize: '80vw', lineHeight: 1 }}>🕉️</div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <RevealLeft>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-6">धाम दर्शन हेतु संपर्क</h2>
              <ul className="space-y-4 text-white/85">
                <li className="flex items-start gap-3">
                  <span className="text-xl flex-shrink-0 mt-0.5">📍</span>
                  <span className="text-sm sm:text-base">ग्राम + पोo — फुलहर धाम, हरलाखी, मधुबनी — 847240 (बिहार)</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">📞</span>
                  <a href="tel:+919470820521" className="text-sm sm:text-base hover:text-yellow-300 transition-colors font-semibold">
                    +91 9470820521
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">✉️</span>
                  <a href="mailto:shreesitaramjimandir@gmail.com"
                    className="text-sm sm:text-base hover:text-yellow-300 transition-colors break-all">
                    shreesitaramjimandir@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-xl">🕒</span>
                  <span className="text-sm">प्रतिदिन प्रातः 6:00 — संध्या 7:00 बजे</span>
                </li>
              </ul>
            </RevealLeft>

            <RevealRight>
              <div className="rounded-3xl p-6 sm:p-8"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}>
                <div className="text-4xl text-center mb-4">💰</div>
                <h3 className="font-serif text-xl font-bold text-white text-center mb-3">दान एवं सहयोग</h3>
                <p className="text-white/70 text-sm text-center mb-6 leading-relaxed">
                  मंदिर निर्माण और श्रद्धालुओं की सुविधाओं के लिए आपके सहयोग की आवश्यकता है।
                </p>
                <div className="om-divider"><span>🕉️</span></div>
                <Link to="/donate"
                  className="block w-full mt-6 py-3.5 text-center font-bold rounded-2xl text-sm btn-donation">
                  सहयोग करें — दान करें
                </Link>
                <p className="text-white/50 text-[10px] text-center mt-3">
                  80G कर छूट के लिए पात्र · पारदर्शी उपयोग
                </p>
              </div>
            </RevealRight>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
